import {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { type Unsubscribe } from 'firebase/firestore';
import { DashResume, KpiResume } from './types';

import Drawer from '../../juristec-ui/core/Drawer';
import Button from '../../juristec-ui/core/Button';
import MiniLoading from '../../juristec-ui/core/MiniLoading';
import Tooltip from '../../juristec-ui/core/Tooltip';
import AssistentTokensBadge, { type GptTokens } from '../AssistentTokensBadge/AssistentTokensBadge';
import ResumesChat from './ResumesChat';

import { formatDateTime } from '../../juristec-ui/utils/functions/lab';

import {
  ExpandMoreWide,
} from '../../juristec-ui/icons';

import {
  HeaderContainer,
  TimelineContainer,
  MainContainer,
  TimelineTimestamps,
  SubHeader,
  ActionFooter,
  LoadingWrapper,
  GPTCredits,
  EmptyMsg,
} from './styled/index.styled';

interface VSResumeDrawerProps {
  isOpen: boolean;
  close: () => void;
  gptTokens: GptTokens;
  kpis: Record<string, string>[];
  dashResumes: Map<string, DashResume> | null;
  loadDashResumes: () => Promise<Map<string, DashResume> | null>;
  loadKpisResumes: (resumeKey: string) => Promise<Map<string, KpiResume> | null>;
  genDashKpisResumes: () => Promise<string | null>;
  setListener: (resumeKey: string) => Promise<Unsubscribe | null>;
  showKpiFromResume: (timestamp: string, resume: KpiResume) => void;
  hideGeneration: boolean;
  blockGeneration: boolean;
}

const VSResumeDrawer = ({
  isOpen,
  close,
  gptTokens,
  kpis,
  dashResumes,
  loadDashResumes,
  loadKpisResumes,
  genDashKpisResumes,
  setListener,
  showKpiFromResume,
  hideGeneration,
  blockGeneration,
}: VSResumeDrawerProps) => {
  const validKpis = useMemo(() => kpis.reduce((aux, k) => {
    if (['Iframe', 'SubsetTable', 'label'].includes(k.type)) return aux;
    return aux.set(k.id, k);
  }, new Map()), [kpis]);
  const detachListener = useRef<Unsubscribe>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(true);
  const [selected, setSelected] = useState<string | null>(null);
  const deselect = () => setSelected(null);

  const handleSelect = async (dashKey: string) => {
    setSelected(dashKey);
    const dashRes = dashResumes?.get(dashKey);
    if (dashRes?.resumes === null) {
      await loadKpisResumes(dashRes.id);
    }
  };

  useEffect(() => {
    if (dashResumes !== null) {
      const isDoing = [...dashResumes.values()].find((r) => r.status === 'doing');
      if (!isDoing) {
        setIsGenerating(false);
        if (detachListener.current) detachListener.current();
      }
    }
  }, [dashResumes]);

  useEffect(() => {
    (async () => {
      if (isOpen) {
        const res = await loadDashResumes();
        if (res) {
          const isDoing = [...res.values()].find((r) => r.status === 'doing');
          if (isDoing) {
            setIsGenerating(true);
            detachListener.current = await setListener(isDoing.id);
          } else setIsGenerating(false);
        }
      }
    })();
    return () => {
      if (detachListener.current) detachListener.current();
      deselect();
    };
  }, [isOpen]);

  const generateResume = async () => {
    setIsGenerating(true);
    const res = await genDashKpisResumes();
    if (res) {
      setSelected(res);
      detachListener.current = await setListener(res);
    }
  };

  return (
    <Drawer
      open={isOpen}
      direction="left"
      handleCloseDrawer={close}
      // @ts-expect-error not typed yet
      customWidth="25rem"
    >
      <MainContainer>
        <HeaderContainer>
          <h2>Resumo de Dashboard</h2>
        </HeaderContainer>
        {selected === null ? (
          <>
            <SubHeader>
              <h5>
                Últimos resumos:
              </h5>
            </SubHeader>
            {(dashResumes === null || dashResumes.size <= 0) && (
              <EmptyMsg>Ainda não há resumos gerados</EmptyMsg>
            )}
            <TimelineContainer>
              {dashResumes !== null && Array.from(dashResumes).map(([key, dashRes], i, arr) => (
                <TimelineTimestamps key={key} onClick={() => handleSelect(key)}>
                  {isGenerating && i === arr.length - 1 && (
                    <LoadingWrapper style={{ position: 'absolute', left: '0' }}>
                      <MiniLoading fill="primary" size="small" />
                    </LoadingWrapper>
                  )}
                  <span>{formatDateTime(dashRes.createdAt, { time: 'half' })}</span>
                  <ExpandMoreWide />
                </TimelineTimestamps>
              ))}
            </TimelineContainer>
            {!hideGeneration && (
              <ActionFooter>
                <div className="token-row">
                  <span style={{ marginRight: 'auto', fontSize: '12px' }}>
                    {`*${validKpis.size + 1} tokens por resumo válido`}
                  </span>
                  <AssistentTokensBadge
                    gptTokens={gptTokens}
                  />
                </div>
                <Tooltip
                  textWhen={blockGeneration}
                  text="O administrador desativou o uso deste agente para sua conta"
                  className="gen-button-tooltip"
                  direction="top"
                >
                  <Button
                    style={{ width: '100%' }}
                    onClick={generateResume}
                    disabled={
                      isGenerating
                      || (
                        1 + validKpis.size - gptTokens.bonus > 0
                        && gptTokens.used > gptTokens.limit - (1 + validKpis.size - gptTokens.bonus)
                      )
                      || blockGeneration
                    }
                  >
                    Gerar novo resumo
                  </Button>
                </Tooltip>
                <GPTCredits style={{ display: 'block' }}>
                  <span>
                    Alimentado pelo
                    {' '}
                  </span>
                  <a
                    href="https://openai.com/pt-BR/index/gpt-4o-mini-advancing-cost-efficient-intelligence/"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    GPT-4o mini
                  </a>
                </GPTCredits>
              </ActionFooter>
            )}
          </>
        ) : (
          <ResumesChat
            dashResumes={dashResumes ?? new Map()}
            selected={selected}
            deselect={deselect}
            kpis={validKpis}
            showKpi={showKpiFromResume}
          />
        )}
      </MainContainer>
    </Drawer>
  );
};

export default VSResumeDrawer;
